import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import styled from 'styled-components';
import LoginView, { LoginResult, MESSAGES } from './LoginView';

type ApiFetch = (path: string, init?: RequestInit) => Promise<Response>;

type AuthContextValue = {
  deliveryName: string;
  logout: () => Promise<void>;
  apiFetch: ApiFetch;
};

type AuthState =
  | { status: 'loading' }
  | { status: 'authenticated'; deliveryName: string }
  | { status: 'unauthenticated'; notice?: string }
  | { status: 'unavailable' };

const CSRF_HEADER = { 'X-DS-CSRF': '1' };

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

const StatusPage = styled.main`
  font-family: Roboto, sans-serif;
  padding: 32px;
`;

const baseUrl = () => process.env.REACT_APP_HTTP_GET_SIGNING_API_URL;

type SessionCheck =
  | { status: 'authenticated'; deliveryName: string }
  | { status: 'unauthenticated' }
  | { status: 'unavailable' };

async function checkSession(): Promise<SessionCheck> {
  try {
    const response = await fetch(`${baseUrl()}/auth/session`, {
      credentials: 'include',
    });

    if (response.status === 401) return { status: 'unauthenticated' };
    if (response.status !== 200) return { status: 'unavailable' };

    const body = await response.json();
    if (typeof body?.deliveryName === 'string' && body.deliveryName) {
      return { status: 'authenticated', deliveryName: body.deliveryName };
    }
    return { status: 'unavailable' };
  } catch {
    return { status: 'unavailable' };
  }
}

export function SessionAuthProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<AuthState>({ status: 'loading' });
  const mounted = useRef(true);
  // Incremented whenever the session changes so late 401s from old sessions are ignored.
  const generation = useRef(0);

  const update = useCallback((next: AuthState) => {
    generation.current += 1;
    if (mounted.current) setState(next);
  }, []);

  useEffect(() => {
    mounted.current = true;
    void checkSession().then((result) => {
      if (mounted.current) update(result);
    });
    return () => {
      mounted.current = false;
    };
  }, [update]);

  const apiFetch = useCallback<ApiFetch>(async (path, init) => {
    const callGeneration = generation.current;
    const headers = new Headers(init?.headers);
    headers.delete('Authorization');

    const response = await fetch(`${baseUrl()}${path}`, {
      ...init,
      headers,
      credentials: 'include',
    });

    if (response.status === 401 && callGeneration === generation.current) {
      update({ status: 'unauthenticated', notice: MESSAGES.expired });
    }

    return response;
  }, [update]);

  const login = useCallback(
    async (username: string, password: string): Promise<LoginResult> => {
      try {
        const response = await fetch(`${baseUrl()}/auth/session`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json', ...CSRF_HEADER },
          body: JSON.stringify({ username, password }),
        });

        if (response.status === 401) return 'invalid';
        if (response.status === 429) return 'rate_limited';
        if (response.status !== 204) return 'unavailable';

        const session = await checkSession();
        if (session.status !== 'authenticated') return 'unavailable';

        if (!window.location.pathname.startsWith(`/${session.deliveryName}`)) {
          window.history.replaceState(null, '', '/');
        }
        update(session);
        return undefined;
      } catch {
        return 'unavailable';
      }
    },
    [update]
  );

  const logout = useCallback(async () => {
    try {
      await fetch(`${baseUrl()}/auth/logout`, {
        method: 'POST',
        credentials: 'include',
        headers: CSRF_HEADER,
      });
    } catch {
      // The local session view is always cleared below.
    }
    update({ status: 'unauthenticated' });
  }, [update]);

  switch (state.status) {
    case 'loading':
      return <StatusPage role="status">Loading…</StatusPage>;
    case 'unavailable':
      return (
        <StatusPage role="alert">
          <h1>Sign-in is temporarily unavailable. Try again later.</h1>
        </StatusPage>
      );
    case 'unauthenticated':
      return <LoginView notice={state.notice} onLogin={login} />;
    default:
      return (
        <AuthContext.Provider
          value={{ deliveryName: state.deliveryName, logout, apiFetch }}
        >
          {children}
        </AuthContext.Provider>
      );
  }
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used inside an authenticated SessionAuthProvider.');
  }
  return context;
}
